/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useMemo } from "react";
import type { DoodleBadgeId } from "./badges.data";
import { doodleBadgeMarkup, ensureDoodleMotionStyles } from "./render";

/**
 * One badge from the Doodles library — the redesign's mark for BIG things:
 * section headings in the sidebar, page headers, answer tiles, drawer headers
 * and empty states. Small things (nav children, buttons, filters, inputs) take
 * a line icon instead; the two are never mixed at one size.
 *
 * Decorative by default: the label beside it carries the meaning, so the SVG is
 * hidden from assistive tech. Pass `label` only when the badge stands alone.
 *
 * The markup comes from the generated, checked-in `badges.data.ts` — static
 * strings this app ships, never user input — which is what makes
 * `dangerouslySetInnerHTML` safe here.
 */
export function DoodleBadge({
  id,
  size = 24,
  animate = true,
  label,
  className,
}: {
  id: DoodleBadgeId;
  /** Rendered box in px. The drawing overhangs it by its 2px shadow. */
  size?: number;
  /** Idle motion (blinks, steam, a ringing bell). Off for lists of many. */
  animate?: boolean;
  label?: string;
  className?: string;
}) {
  const markup = useMemo(() => doodleBadgeMarkup(id, animate), [id, animate]);
  useEffect(() => {
    if (animate) ensureDoodleMotionStyles();
  }, [animate]);
  if (!markup) return null;
  return (
    <svg
      data-ui="doodle-badge"
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      stroke="#1d1d1f"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ display: "block", flex: "none", overflow: "visible" }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}
